import React, { useEffect, useState } from 'react';
import Dialog from 'Components/UI/Dialog';
import { noop } from 'Helpers/PropTypes';
import { DialogContent } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import useMobileLayout from 'Hooks/useMobileLayout';
import Sentry from 'Integrations/Sentry';
import SentryScopedError from 'Errors/SentryScopedError';
import LinkExternalAccount from 'Components/UI/LinkExternalAccount';
import fr from 'facet-rest';

const useStyles = makeStyles(theme => ({
  modal: {
    height: '800px',
  },
  panelSummary: {
    paddingLeft: '0',
    paddingRight: '0',
  },
  summaryTitle: {
    flexGrow: 1,
  },
  panelDetail: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  managedAccountsContainer: {
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  managedRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  managedDescription: {
    color: theme.palette.facet.primaryGray,
  },
  managedCurrency: {
    flexGrow: '1',
    color: theme.palette.facet.accentGreen,
    fontSize: '37px',
    fontWeight: 'lighter',
  },
  managedEquity: {
    flexDirection: 'column',
    color: theme.palette.facet.primaryBlue,
    fontWeight: 'bold',
  },
  managedFixed: {
    flexDirection: 'column',
    color: theme.palette.facet.accentBlue,
    fontWeight: 'bold',
    paddingLeft: '12px',
  },
  emptyMessage: {
    color: theme.palette.facet.primaryGray,
    whiteSpace: 'pre-wrap',
    textAlign: 'center',
  },
  linkButton: {
    marginLeft: 'auto',
  },
  footer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  closeButton: {
    display: 'flex',
  },
  invalidBalancesButton: {
    fill: theme.palette.error.main,
    borderRadius: theme.radius.md,
    fontWeight: theme.typography.fontWeightBold,
    marginLeft: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  titleBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  addIcon: {
    marginRight: theme.spacing(1),
    padding: theme.spacing(0.25),
  },
}));

export function sumAccountBalances(accounts = [], logToSentry = false) {
  let invalidBalances = 0;
  const totalBalance = accounts.reduce((total, account) => {
    if (account.attributes.disabled === 'false') {
      const balance = parseFloat(account.attributes.balance);
      if (!Number.isFinite(balance)) {
        // FW-1573: We learned through this that it is possible for balances
        // to come back and be undefined when a balance is stored as "null"
        // If a non-undefined balance comes back, then we have a new problem and should log that.
        if (logToSentry && balance !== undefined) {
          Sentry.createSentryErrorLog(
            new SentryScopedError('account-balance-nan', {
              accountId: account.id,
              accountBalance: account.attributes.balance,
            }),
          );
        }
        invalidBalances++;
        return total;
      }
      if (account.attributes.accountType === 'liability') {
        return total - balance;
      }
      return total + balance;
    }
    return total;
  }, 0);

  return {
    totalBalance,
    invalidBalances,
  };
}

const getFinicityLinkHandler = async (finicityLinkUrl, finicityLink) => {
  if (finicityLink) {
    const coll = await finicityLink.get();
    const [finicityLinkRes] = coll;
    return {
      url: finicityLinkRes.attr('url'),
      onSuccessUrl: finicityLinkRes.relationship(fr.relationships.syncFinicityAccounts.name).link('related'),
    };
  }

  return { url: finicityLinkUrl };
};

function FinicityModal({
  finicityLinkUrl,
  finicityLink,
  onClose,
  onComplete,
  onSyncStarted,
}) {
  const classes = useStyles();
  const isMobile = useMobileLayout();
  const [finicityLinkURL, setFinicityLinkURL] = useState(null);

  useEffect(() => {
    const cb = async () => {
      const handler = await getFinicityLinkHandler(finicityLinkUrl, finicityLink);
      setFinicityLinkURL(handler);
    };
    cb();
  }, [finicityLinkUrl, finicityLink]);

  if (!finicityLinkURL) {
    return null;
  }

  const handleOnClose = async () => {
    if (onClose) {
      await onClose();
    }
  };

  const handleOnComplete = async () => {
    await handleOnClose();

    if (finicityLinkURL.onSuccessUrl) {
      if (onSyncStarted) {
        await onSyncStarted();
      }
      await finicityLinkURL.onSuccessUrl.post({ raw: true, deprecatedAllowAlterOfRelation: true });
    }

    if (onComplete) {
      await onComplete();
    }
  };

  return (
    <Dialog
      maxWidth="md"
      width="md"
      fullScreen={isMobile}
      fullWidth
      scroll="body"
      open
      onClose={handleOnClose}
      disableEnforceFocus
      showCloseIcon={false}
    >
      <DialogContent className={classes.modal}>
        <LinkExternalAccount
          onComplete={handleOnComplete}
          onCancel={handleOnClose}
          finicityLinkUrl={finicityLinkURL.url}
        />
      </DialogContent>
    </Dialog>
  );
}

FinicityModal.propTypes = {
  onClose: PropTypes.func,
  finicityLinkUrl: PropTypes.string,
  finicityLink: PropTypes.string,
  onComplete: PropTypes.func,
  onSyncStarted: PropTypes.func,
};

FinicityModal.defaultProps = {
  finicityLinkUrl: null,
  finicityLink: null,
  onClose: noop,
  onComplete: noop,
  onSyncStarted: noop,
};

export default FinicityModal;
